import { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { formatCurrency } from "@/lib/api";

/**
 * Leaflet map of clients with latitude/longitude set.
 * - Plusieurs clients d'une même ville sont légèrement décalés (spiderfy déterministe)
 *   pour qu'ils ne se superposent pas exactement.
 * - Les panes Leaflet sont forcées à z-index < 50 pour passer sous les Dialog shadcn.
 */
export const ClientsMap = ({ items, onClientClick }) => {
  const points = useMemo(() => {
    const rawPoints = (items || []).filter(
      (c) => typeof c.latitude === "number" && typeof c.longitude === "number",
    );

    // Grouper par coordonnée (arrondie à 4 décimales ~ 11m) pour détecter
    // les "clusters" (clients dans la même ville / même point)
    const groups = new Map();
    for (const c of rawPoints) {
      const key = `${c.latitude.toFixed(4)},${c.longitude.toFixed(4)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }

    const out = [];
    for (const group of groups.values()) {
      if (group.length === 1) {
        out.push({ ...group[0] });
        continue;
      }
      // Spiderfy : répartir sur un cercle de ~400m (env 0.004° lat)
      const n = group.length;
      const radiusLat = 0.005; // ~550m
      group.forEach((c, i) => {
        const angle = (2 * Math.PI * i) / n;
        // Compensation longitude à cette latitude (cosinus)
        const radiusLng = radiusLat / Math.cos((c.latitude * Math.PI) / 180);
        out.push({
          ...c,
          latitude: c.latitude + radiusLat * Math.sin(angle),
          longitude: c.longitude + radiusLng * Math.cos(angle),
          _spiderfy: true,
        });
      });
    }
    return out;
  }, [items]);

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center h-[320px] rounded-lg border bg-muted/30 text-sm text-muted-foreground">
        Aucun client géolocalisé. Cliquez sur "Géolocaliser tout" pour résoudre les villes des clients.
      </div>
    );
  }

  const centre = [46.5, 2.5];
  return (
    <div
      className="rounded-lg border overflow-hidden isolate relative z-0"
      data-testid="clients-map"
    >
      {/* Forcer les panes Leaflet à rester sous les Dialog (shadcn utilise z-50). */}
      <style>{`
        .clients-map-container .leaflet-pane,
        .clients-map-container .leaflet-top,
        .clients-map-container .leaflet-bottom,
        .clients-map-container .leaflet-control { z-index: 10 !important; }
      `}</style>
      <MapContainer
        className="clients-map-container"
        center={centre}
        zoom={5.5}
        style={{ height: 420, width: "100%", zIndex: 0 }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((c) => {
          const color = c.at_risk_outstanding > 0 ? "#dc2626" : c.total_ht > 0 ? "#4f46e5" : "#64748b";
          return (
            <CircleMarker
              key={c.key}
              center={[c.latitude, c.longitude]}
              radius={c.total_ht > 10000 ? 10 : c.total_ht > 2000 ? 7 : 5}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.65, weight: 1.5 }}
            >
              <Popup>
                <div className="space-y-1 text-xs">
                  <div className="font-semibold text-sm">{c.client_name}</div>
                  {c.client_email && <div className="text-slate-600">{c.client_email}</div>}
                  {c.phone && <div className="text-slate-600">{c.phone}</div>}
                  {c.profile_city && <div className="text-slate-600">{c.profile_city} ({c.postal_code || "—"})</div>}
                  <div className="pt-1 flex items-center gap-2">
                    <span className="font-mono text-indigo-700">{formatCurrency(c.total_ht || 0)} HT</span>
                    {c.outstanding > 0 && <span className="font-mono text-red-700">+ {formatCurrency(c.outstanding)} dû</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => onClientClick?.(c.key)}
                    className="text-indigo-700 underline cursor-pointer"
                    data-testid={`map-open-${c.key}`}
                  >
                    Ouvrir la fiche →
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ClientsMap;
